/**
 * KAN-862: чистые функции канбан-доски CRM: состояние доски в адресе,
 * оптимистичный перенос карточки.
 * KAN-880: колонки из ответа (columns), карточки по card.column, склонение дней.
 * KAN-882: доска в 4 видах (?board=work|managers|temperature|funnel). Колонки
 * только из columns ответа, карточки в порядке ответа (backend отсортировал).
 * Перенос исполняет accepts/target целевой колонки (resolveCardDrop): фронт
 * правил видов не знает. Мобильные вкладки: свёрнутые в конце, по умолчанию
 * не свёрнутая "Требуют внимания" (KAN-886), иначе первая не свёрнутая с
 * карточками. Фильтры в адресе: crmLeadFilters.
 */
import type { AttentionAction, AttentionItem } from '@/services/api/dashboard';
import type {
  CrmBoardColumn,
  CrmBoardView,
  CrmCardState,
  CrmColumnAccepts,
  CrmPipelineCard,
  CrmPipelineOutcome,
  CrmPipelineResponse,
  CrmPipelineStage,
  CrmStageCardsPage,
  CrmStageId,
} from '@/types/crmPipeline';

export const NO_STAGE_TITLE = 'Без этапа';

export function sortStages(stages: readonly CrmPipelineStage[]): CrmPipelineStage[] {
  return [...stages].sort((a, b) => a.order - b.order);
}

export const sameStage = (a: CrmStageId | null | undefined, b: CrmStageId | null | undefined): boolean =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

/* Виды доски */

export const CRM_BOARD_VIEW_OPTIONS: readonly { value: CrmBoardView; label: string }[] = [
  { value: 'work', label: 'Работа' },
  { value: 'managers', label: 'Менеджеры' },
  { value: 'temperature', label: 'Теплота' },
  { value: 'funnel', label: 'Воронка' },
];

export const DEFAULT_BOARD_VIEW: CrmBoardView = 'work';

/** ?board= -> вид; неизвестное и пустое -> work. */
export function parseBoardView(value: string | null): CrmBoardView {
  const found = CRM_BOARD_VIEW_OPTIONS.find((o) => o.value === value);
  return found ? found.value : DEFAULT_BOARD_VIEW;
}

/** Виды, где не все карточки "Требуют внимания" и нет такой колонки: фильтр и метка состояния нужны. */
const MIXED_VIEWS: readonly CrmBoardView[] = ['managers', 'funnel'];

/** Кнопка "Требуют внимания (N)" и ?attention=1 только в видах managers и funnel. */
export const boardHasAttentionFilter = (view: CrmBoardView): boolean => MIXED_VIEWS.includes(view);

/** Метка состояния на карточке только в видах managers и funnel. */
export const boardShowsCardState = (view: CrmBoardView): boolean => MIXED_VIEWS.includes(view);

export const CARD_STATE_LABELS: Record<CrmCardState, string> = {
  attention: 'Требует внимания',
  taken: 'Взят в работу',
  bot: 'У бота',
  cold: 'Холодный',
  worked: 'Отработан',
  refused: 'Отказ',
};

/** KAN-921: пометка "кто закрыл" по outcome_source; пусто и неизвестное: пометки нет. */
const CLOSED_BY_LABELS: Record<string, string> = {
  bot: 'Закрыл бот',
  manual: 'Закрыл менеджер',
  call: 'Закрыл менеджер',
  order: 'Заказ отменён',
};

export function cardClosedByLabel(source: string | null | undefined): string | null {
  return (source && CLOSED_BY_LABELS[source]) || null;
}

/** Пометка исхода на карточке: "Успех" / "Не успех"; у открытых нет. */
export function cardOutcomeLabel(outcome: CrmPipelineOutcome): string | null {
  if (outcome === 'won') return 'Успех';
  if (outcome === 'lost') return 'Не успех';
  return null;
}

/* Колонки */

/** Колонки доски из columns ответа (порядок, подписи, счётчики); карточки по card.column в порядке ответа. */
export function buildApiColumns(data: Pick<CrmPipelineResponse, 'columns' | 'cards'>): CrmBoardColumn[] {
  return (data.columns ?? []).map((column) => ({
    key: column.key,
    title: column.label,
    count: column.count,
    collapsed: column.collapsed,
    kind: column.kind,
    accepts: column.accepts,
    target: column.target,
    cards: data.cards.filter((card) => card.column === column.key),
  }));
}

/** Колонка принимает перенос карточки. */
export const acceptsDrop = (column: Pick<CrmBoardColumn, 'accepts'>): boolean => column.accepts !== null;

/** Карточку можно тянуть: лид открыт и есть другая колонка, принимающая перенос. */
export function canDragCard(
  card: Pick<CrmPipelineCard, 'outcome'>,
  column: Pick<CrmBoardColumn, 'key'>,
  columns: readonly CrmBoardColumn[]
): boolean {
  return card.outcome === 'open' && columns.some((c) => c.key !== column.key && acceptsDrop(c));
}

/** Есть ли в виде колонки этапов (кнопка "Сменить этап" на телефоне). */
export const boardChangesStage = (columns: readonly CrmBoardColumn[]): boolean =>
  columns.some((c) => c.accepts === 'stage');

/** Колонка, принимающая перенос этого типа с этим target (например, этап из "Сменить этап"). */
export function columnForTarget(
  columns: readonly CrmBoardColumn[],
  accepts: Exclude<CrmColumnAccepts, null>,
  target: string | number
): CrmBoardColumn | undefined {
  return columns.find((c) => c.accepts === accepts && c.target !== null && String(c.target) === String(target));
}

/** Мобильные вкладки: не свёрнутые в порядке ответа, затем свёрнутые (копия, вход не меняется). */
export function mobileTabOrder(columns: readonly CrmBoardColumn[]): CrmBoardColumn[] {
  return [...columns.filter((c) => !c.collapsed), ...columns.filter((c) => c.collapsed)];
}

/**
 * Колонка по умолчанию: не свёрнутая "Требуют внимания" (kind attention), даже пустая
 * и не первая (KAN-886); иначе первая не свёрнутая с карточками; иначе первая
 * не свёрнутая; иначе первая.
 */
export function defaultColumnKey(columns: readonly CrmBoardColumn[]): string | null {
  const open = columns.filter((c) => !c.collapsed);
  const attention = open.find((c) => c.kind === 'attention');
  return (attention ?? open.find((c) => c.count > 0) ?? open[0] ?? columns[0])?.key ?? null;
}

/* Перенос карточки */

export type CrmCardDrop =
  | { type: 'take' }
  | { type: 'manager'; managerId: string }
  | { type: 'temperature'; level: string }
  | { type: 'stage'; stageId: CrmStageId }
  | null;

/** Перенос с запросом изменения клиента (кроме "Взять в работу"). */
export type CrmCardEdit = Exclude<CrmCardDrop, { type: 'take' } | null>;

/** Перенос в колонку по её accepts/target. */
function dropFor(to: CrmBoardColumn): CrmCardDrop {
  if (to.accepts === 'take') return { type: 'take' };
  if (to.target === null) return null;
  if (to.accepts === 'manager') return { type: 'manager', managerId: String(to.target) };
  if (to.accepts === 'temperature') return { type: 'temperature', level: String(to.target) };
  if (to.accepts === 'stage') return { type: 'stage', stageId: to.target };
  return null;
}

/**
 * Что сделать при переносе карточки: исполняется accepts/target целевой
 * колонки. Ничего: нет колонки, та же колонка, колонка не принимает, лид закрыт.
 */
export function resolveCardDrop(
  card: Pick<CrmPipelineCard, 'outcome'>,
  from: CrmBoardColumn | undefined,
  to: CrmBoardColumn | undefined
): CrmCardDrop {
  if (!from || !to || from.key === to.key || !acceptsDrop(to) || card.outcome !== 'open') return null;
  return dropFor(to);
}

/** Поля карточки после переноса (оптимистично, до ответа сервера). */
export function cardEditPatch(edit: CrmCardEdit, title: string | null): Partial<CrmPipelineCard> {
  if (edit.type === 'manager') return { manager: { id: edit.managerId, name: title ?? '' } };
  if (edit.type === 'temperature') return { temperature: edit.level as CrmPipelineCard['temperature'] };
  return title === null ? { stage_id: edit.stageId } : { stage_id: edit.stageId, stage_name: title };
}

/**
 * Оптимистичный перенос карточки в колонку: card.column, поля patch и счётчики
 * columns. Карточки нет в ответе (порция колонки): счётчики по fromColumn.
 * toColumn null: колонка не меняется, только поля. Ничего не меняется или
 * ответа без columns: тот же объект.
 */
export function moveCardToColumn(
  data: CrmPipelineResponse,
  clientId: string,
  toColumn: string | null,
  fromColumn?: string | null,
  patch: Partial<CrmPipelineCard> = {}
): CrmPipelineResponse {
  if (!data.columns) return data;
  const card = data.cards.find((c) => c.id === clientId);
  const from = card?.column ?? fromColumn ?? null;
  const moving = toColumn !== null && from !== toColumn;
  if (!moving && Object.keys(patch).length === 0) return data;
  return {
    ...data,
    columns: moving
      ? data.columns?.map((column) => {
          if (column.key === from) return { ...column, count: Math.max(0, column.count - 1) };
          if (column.key === toColumn) return { ...column, count: column.count + 1 };
          return column;
        })
      : data.columns,
    cards: data.cards.map((c) => (c.id === clientId ? { ...c, ...patch, ...(moving ? { column: toColumn } : {}) } : c)),
  };
}

/** Карточка уходит с доски: карточка и единица счётчика её колонки. */
function withoutCard(data: CrmPipelineResponse, clientId: string): CrmPipelineResponse {
  const card = data.cards.find((c) => c.id === clientId);
  if (!card) return data;
  return {
    ...data,
    columns: data.columns?.map((column) =>
      column.key === card.column ? { ...column, count: Math.max(0, column.count - 1) } : column
    ),
    cards: data.cards.filter((c) => c.id !== clientId),
  };
}

const columnOfKind = (data: CrmPipelineResponse, kind: string) => data.columns?.find((c) => c.kind === kind);

/**
 * Оптимистичное действие "Взять в работу" / "Успех" / "Не успех": карточка
 * переезжает в колонку взятых или отработанных, если она есть в виде; KAN-921:
 * "Не успех" в колонку отказов (kind refused), без неё (старый backend) в
 * отработанные, как раньше; исход в
 * виде без отработанных убирает карточку (там только открытые). Остальное
 * поправит обновление доски после ответа.
 * "Взять" в виде без колонки взятых карточку оставляет (снимает внимание):
 * по колонкам ответа не видно, остаётся ли взятый лид в этом виде (в
 * "Менеджерах" и "Воронке" остаётся, в "Теплоте" нет), а знать правила видов
 * фронту нельзя; ответ backend после действия уберёт её, где нужно.
 */
export function applyPipelineAction(
  data: CrmPipelineResponse,
  clientId: string,
  action: AttentionAction
): CrmPipelineResponse {
  const now = new Date().toISOString();
  // KAN-955: "Работа идёт" снимает только внимание, карточка остаётся в своей колонке.
  if (action === 'work_continues') return moveCardToColumn(data, clientId, null, null, { attention: false });
  if (action === 'done') {
    const taken = columnOfKind(data, 'taken');
    const card = data.cards.find((c) => c.id === clientId);
    return moveCardToColumn(data, clientId, taken?.key ?? null, null, {
      attention: false,
      state: 'taken',
      taken_at: card?.taken_at ?? now,
    });
  }
  const worked = columnOfKind(data, 'worked');
  const refused = action === 'refused' ? columnOfKind(data, 'refused') : undefined;
  if (refused) {
    return moveCardToColumn(data, clientId, refused.key, null, {
      attention: false,
      state: 'refused',
      outcome: 'lost',
      outcome_at: now,
      outcome_source: 'manual',
    });
  }
  if (!worked) return withoutCard(data, clientId);
  return moveCardToColumn(data, clientId, worked.key, null, {
    attention: false,
    state: 'worked',
    outcome: action === 'success' ? 'won' : 'lost',
    outcome_at: now,
  });
}

/** Порции колонки в кеше (useInfiniteQuery). */
export interface CrmColumnPagesData {
  pages: CrmStageCardsPage[];
  pageParams: unknown[];
}

/** Карточка из догруженных порций колонки. */
export function findCardInPages(
  datasets: readonly (CrmColumnPagesData | undefined)[],
  clientId: string
): CrmPipelineCard | undefined {
  for (const data of datasets) {
    const found = data?.pages.flatMap((p) => p.cards).find((c) => c.id === clientId);
    if (found) return found;
  }
  return undefined;
}

/** Оптимистично убрать карточку из порций колонки (перенесли в другую). */
export function withoutCardInPages(data: CrmColumnPagesData, clientId: string): CrmColumnPagesData {
  return {
    ...data,
    pages: data.pages.map((page) => {
      const cards = page.cards.filter((c) => c.id !== clientId);
      return cards.length === page.cards.length ? page : { ...page, cards, total: Math.max(0, page.total - 1) };
    }),
  };
}

/** Карточка доски как элемент блока внимания (окно "Назначить в работу"). */
export function pipelineCardToAttentionItem(card: CrmPipelineCard): AttentionItem {
  return {
    client_id: card.id,
    name: card.name,
    company: card.company?.name ?? '',
    reasons: [],
    reason_text: card.attention_reason ?? '',
    since: card.attention_since ?? '',
    last_message: null,
    sender_account: null,
    estimate_id: null,
    manager: card.manager ? { id: card.manager.id, full_name: card.manager.name } : null,
    task: card.attention_task,
  };
}

/* KAN-880: дни на этапе и в работе */

/** "1 день", "2 дня", "5 дней", "11 дней", "21 день". */
export function formatDaysCount(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'дней';
  if (mod10 === 1 && mod100 !== 11) word = 'день';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'дня';
  return `${n} ${word}`;
}

function daysText(n: number | null | undefined, suffix: string): string | null {
  if (n === null || n === undefined) return null;
  return n <= 0 ? `меньше дня ${suffix}` : `${formatDaysCount(n)} ${suffix}`;
}

/** "N дней на этапе"; 0: "меньше дня на этапе"; null: подписи нет. */
export const daysOnStageText = (n: number | null | undefined): string | null => daysText(n, 'на этапе');

/** "N дней в работе"; 0: "меньше дня в работе"; null: подписи нет. */
export const daysInWorkText = (n: number | null | undefined): string | null => daysText(n, 'в работе');

export { crmLeadFilterParams, hasCrmLeadFilters, parseCrmLeadFilters } from './crmLeadFilters';

/* Состояние страницы в адресе: ?view=&board=&active= (исход ?outcome= только у таблицы) */

/** KAN-883: stats - "Лиды по дням". */
export type CrmView = 'table' | 'board' | 'stats';

/** Исход в таблице CRM (на доске переключателя исхода нет). */
export const CRM_OUTCOME_OPTIONS: readonly { value: CrmPipelineOutcome; label: string }[] = [
  { value: 'open', label: 'Открытые' },
  { value: 'won', label: 'Успех' },
  { value: 'lost', label: 'Отказ' },
];

export function parseCrmView(value: string | null): CrmView | null {
  return value === 'board' || value === 'table' || value === 'stats' ? value : null;
}
